import { FC, Fragment } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BuildOutlined, PlaceOutlined } from '@mui/icons-material';
import { DateTime } from 'luxon';
import type { Barn } from '../../services/barnsService';
import type { Appointment } from './types';
import { getAppointmentSubject } from './subjectLabel';
import { resolveVisitBarnId } from './AppointmentHoverCard';

export interface EquipmentUnit {
  id: string;
  name: string;
  type?: string | null;
}

export interface EquipmentReservation {
  key: string;
  /** Minutes from midnight in the browser's zone, clamped to the day. */
  startMin: number;
  endMin: number;
  members: Appointment[];
  vetId: string | null;
  vetName: string;
  what: string;
  /** Where the unit goes when it leaves the clinic (barn town or name, or the farm). */
  where: string | null;
}

const zone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Each unit's bookings on one day, from that day's appointments (the same rows and clock as the
 * Day grid). A barn visit holds the same window on every horse's appointment, so those collapse
 * into one booking that names the visit.
 */
export const buildEquipmentReservations = (
  units: EquipmentUnit[],
  appointments: Appointment[],
  day: Date,
  barnById: Map<string, Barn>,
): Record<string, EquipmentReservation[]> => {
  const tz = zone();
  const dayStart = DateTime.fromJSDate(day).setZone(tz).startOf('day');
  const fromMs = dayStart.toMillis();
  const toMs = dayStart.plus({ days: 1 }).toMillis();
  const toMin = (iso: string) => {
    const ms = Date.parse(iso);
    if (ms <= fromMs) return 0;
    if (ms >= toMs) return 24 * 60;
    const t = DateTime.fromMillis(ms, { zone: tz });
    return t.hour * 60 + t.minute;
  };
  const byUnit: Record<string, EquipmentReservation[]> = {};
  const index = new Map<string, EquipmentReservation>();
  for (const unit of units) byUnit[unit.id] = [];

  for (const apt of appointments) {
    for (const sch of apt.equipmentSchedules ?? []) {
      if (!byUnit[sch.equipmentId]) continue;
      // Month asks for 42 days over the same holds: drop the ones that miss this day cheaply.
      if (Date.parse(sch.endTime) <= fromMs || Date.parse(sch.startTime) >= toMs) continue;
      const startMin = toMin(sch.startTime);
      const endMin = toMin(sch.endTime);
      if (endMin <= startMin) continue; // a hold that never touches this day
      const key = apt.visitGroupId
        ? `${sch.equipmentId}|${sch.startTime}|${sch.endTime}|${apt.visitGroupId}`
        : `${sch.equipmentId}|${apt.id}`;
      const existing = index.get(key);
      if (existing) {
        existing.members.push(apt);
        continue;
      }
      const barn = barnById.get(resolveVisitBarnId(apt) ?? '') ?? null;
      const location = apt.location?.toLowerCase();
      const offsite = !!(apt.barnId || barn) || location === 'farm';
      const res: EquipmentReservation = {
        key,
        startMin,
        endMin,
        members: [apt],
        vetId: apt.assignedVetId ?? null,
        vetName: apt.assignedVet?.name ?? 'Unassigned',
        what: '',
        where: offsite ? barn?.city || barn?.name || apt.barn?.name || apt.horse?.location || 'Farm call' : null,
      };
      index.set(key, res);
      byUnit[sch.equipmentId].push(res);
    }
  }
  for (const list of Object.values(byUnit)) {
    for (const res of list) {
      const first = res.members[0];
      res.what =
        res.members.length > 1
          ? `${first.barn?.name || barnById.get(resolveVisitBarnId(first) ?? '')?.name || 'Barn visit'} · ${res.members.length} horses`
          : `${getAppointmentSubject(first).name} · ${first.type}`;
    }
    list.sort((a, b) => a.startMin - b.startMin);
  }
  return byUnit;
};

const clock = (min: number, withPeriod = true) => {
  const h24 = Math.floor(min / 60) % 24;
  const text = `${h24 % 12 || 12}:${String(min % 60).padStart(2, '0')}`;
  return withPeriod ? `${text} ${h24 >= 12 ? 'PM' : 'AM'}` : text;
};
// The start drops its AM/PM when the end shares it; an end at midnight is AM.
const rangeText = (s: number, e: number) => `${clock(s, (s >= 720) !== (e % 1440 >= 720))} - ${clock(e)}`;
/**
 * Day, by equipment (Schedule canvas board "Day, by equipment"): a column per active unit, each
 * booking coloured by the provider holding it. An empty slot books that unit at that time.
 */
const EquipmentDayGrid: FC<{
  units: EquipmentUnit[];
  reservations: Record<string, EquipmentReservation[]>;
  timeSlots: Array<{ hour: number; minute: number; isHourMark: boolean }>;
  slotHeight: number;
  timeInterval: number;
  gridStartHour: number;
  gridEndHour: number;
  timeColWidth: number;
  minColWidth: number;
  headerHeight: number;
  showsToday: boolean;
  nowTop: number | null;
  now: Date;
  providerColor: Record<string, { bg: string; fg: string }>;
  onOpenAppointment: (appointment: Appointment) => void;
  /** Undefined when the person may not book. */
  onSlotClick?: (unitId: string, hour: number, minute: number) => void;
}> = ({
  units, reservations, timeSlots, slotHeight, timeInterval, gridStartHour, gridEndHour, timeColWidth, minColWidth,
  headerHeight, showsToday, nowTop, now, providerColor, onOpenAppointment, onSlotClick,
}) => {
  const ppm = slotHeight / timeInterval;
  const gridStart = gridStartHour * 60;
  const gridEnd = (gridEndHour + 1) * 60;
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const status = (list: EquipmentReservation[]) => {
    if (!showsToday) {
      return list.length
        ? { text: `${list.length} booking${list.length === 1 ? '' : 's'}`, busy: false }
        : { text: 'Free all day', busy: false };
    }
    const current = list.find((r) => r.startMin <= nowMin && r.endMin > nowMin);
    if (current) return { text: `In use · ${current.vetName}${current.where ? ', off-site' : ''}`, busy: true };
    const next = list.find((r) => r.startMin > nowMin);
    return { text: next ? `Free until ${clock(next.startMin)}` : 'Free rest of day', busy: false };
  };

  return (
    <Box
      data-testid="equipment-grid"
      sx={{
        display: 'grid',
        gridTemplateColumns: `${timeColWidth}px repeat(${Math.max(units.length, 1)}, minmax(${minColWidth}px, 1fr))`,
        minWidth: 'min-content',
      }}
    >
      <Box sx={{ position: 'sticky', top: 0, left: 0, zIndex: 13, display: 'flex', alignItems: 'flex-end', p: 1, bgcolor: 'background.paper', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Time</Typography>
      </Box>

      {units.map((unit) => {
        const s = status(reservations[unit.id] ?? []);
        return (
          <Box
            key={`head-${unit.id}`}
            sx={{ position: 'sticky', top: 0, zIndex: 12, height: headerHeight, boxSizing: 'border-box', minWidth: 0, px: 1.25, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', bgcolor: 'background.paper', borderLeft: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}
          >
            <Box sx={{ flex: 'none', width: 32, height: 32, borderRadius: 2, bgcolor: '#F3F4F6', color: '#3B2E1A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <BuildOutlined sx={{ fontSize: 17 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 }}>{unit.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontSize: '0.72rem', color: s.busy ? '#7A4314' : '#1D5C38', minWidth: 0 }}>
                <Box component="span" sx={{ flex: 'none', width: 7, height: 7, borderRadius: '50%', bgcolor: s.busy ? '#C9772B' : '#2F8F57' }} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.text}</Box>
              </Box>
            </Box>
          </Box>
        );
      })}

      {/* Time labels, pinned left; sticky cells sit above the booking blocks (z 10). */}
      <Box sx={{ position: 'sticky', left: 0, zIndex: 11, bgcolor: 'background.paper', borderRight: '1px solid #E5E7EB' }}>
        {timeSlots.map((slot, i) => (
          <Box key={`time-${i}`} sx={{ height: `${slotHeight}px`, boxSizing: 'border-box', pr: 1, pt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
            {slot.isHourMark && (
              <Typography sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem', fontWeight: 500, color: 'text.secondary', lineHeight: 1.2 }}>
                {`${slot.hour % 12 || 12} ${slot.hour >= 12 ? 'PM' : 'AM'}`}
              </Typography>
            )}
          </Box>
        ))}
        {showsToday && nowTop !== null && (
          <Box aria-hidden="true" sx={{ position: 'absolute', right: 4, top: nowTop - 9, px: 0.6, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.68rem', fontWeight: 600, lineHeight: '18px' }}>
            {clock(nowMin, false)}
          </Box>
        )}
      </Box>

      {units.map((unit) => (
        <Box key={unit.id} sx={{ position: 'relative', minWidth: 0, height: `${timeSlots.length * slotHeight}px`, borderLeft: '1px solid #E5E7EB' }}>
          {timeSlots.map((slot, i) => (
            <Box
              key={`${unit.id}-slot-${i}`}
              data-testid={`equipment-slot-${unit.id}-${slot.hour}-${slot.minute}`}
              onClick={onSlotClick ? () => onSlotClick(unit.id, slot.hour, slot.minute) : undefined}
              sx={{
                height: `${slotHeight}px`,
                boxSizing: 'border-box',
                borderTop: slot.isHourMark ? '1px solid #E9EBEE' : '1px solid #F6F7F8',
                cursor: onSlotClick ? 'pointer' : 'default',
                '&:hover': onSlotClick ? { bgcolor: '#FBF7F2' } : undefined,
              }}
            />
          ))}
          {(reservations[unit.id] ?? []).map((res) => {
            const start = Math.max(res.startMin, gridStart);
            const end = Math.min(res.endMin, gridEnd);
            if (end <= start) return null;
            const color = res.vetId ? providerColor[res.vetId] : undefined;
            const ink = color?.fg ?? '#374151';
            const time = rangeText(res.startMin, res.endMin);
            return (
              <ButtonBase
                key={res.key}
                aria-label={`${unit.name}, ${res.vetName}, ${time}, ${res.what}${res.where ? `, at ${res.where}` : ''}`}
                onClick={() => onOpenAppointment(res.members[0])}
                sx={{
                  position: 'absolute',
                  left: 4,
                  right: 4,
                  top: (start - gridStart) * ppm + 1,
                  height: Math.max((end - start) * ppm - 3, 22),
                  zIndex: 10,
                  boxSizing: 'border-box',
                  px: 1,
                  py: 0.6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'flex-start',
                  gap: 0.25,
                  overflow: 'hidden',
                  textAlign: 'left',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha(ink, 0.35),
                  bgcolor: color?.bg ?? '#F3F4F6',
                  color: ink,
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(ink, 0.35)}` },
                }}
              >
                <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{time}</Typography>
                <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{res.vetName}</Typography>
                <Typography noWrap sx={{ fontSize: '0.75rem' }}>{res.what}</Typography>
                {res.where && (
                  <Box component="span" sx={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.25, px: 0.75, borderRadius: 999, bgcolor: 'background.paper', fontSize: '0.68rem', fontWeight: 600, color: '#7A4314' }}>
                    <PlaceOutlined sx={{ fontSize: 11 }} />
                    {res.where}
                  </Box>
                )}
              </ButtonBase>
            );
          })}
          {showsToday && nowTop !== null && (
            <Box aria-hidden="true" sx={{ position: 'absolute', left: 0, right: 0, top: nowTop, height: 0, borderTop: '2px solid #C9772B', zIndex: 9, pointerEvents: 'none' }} />
          )}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Week, by equipment: a row per unit and a column per day, each booking a small block in the colour
 * of the provider holding it. A day's header opens that day in Day, by equipment.
 */
export const EquipmentWeekGrid: FC<{
  units: EquipmentUnit[];
  days: Date[];
  /** yyyy-MM-dd (browser zone) → unit id → that day's bookings. */
  byDay: Record<string, Record<string, EquipmentReservation[]>>;
  todayKey: string;
  providerColor: Record<string, { bg: string; fg: string }>;
  onOpenAppointment: (appointment: Appointment) => void;
  onOpenDay: (day: Date) => void;
}> = ({ units, days, byDay, todayKey, providerColor, onOpenAppointment, onOpenDay }) => (
  <Box
    data-testid="equipment-week"
    sx={{ display: 'grid', gridTemplateColumns: `minmax(120px, 160px) repeat(${days.length}, minmax(120px, 1fr))`, minWidth: 'min-content' }}
  >
    <Box sx={{ position: 'sticky', top: 0, left: 0, zIndex: 13, display: 'flex', alignItems: 'flex-end', p: 1, bgcolor: 'background.paper', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Equipment</Typography>
    </Box>
    {days.map((day) => {
      const dt = DateTime.fromJSDate(day);
      const today = dt.toFormat('yyyy-MM-dd') === todayKey;
      return (
        <ButtonBase
          key={`head-${dt.toFormat('yyyy-MM-dd')}`}
          onClick={() => onOpenDay(day)}
          aria-label={`Open ${dt.toFormat('cccc, LLLL d')}`}
          sx={{ position: 'sticky', top: 0, zIndex: 12, py: 1, display: 'flex', flexDirection: 'column', bgcolor: today ? '#FFFAF3' : 'background.paper', borderLeft: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>{dt.toFormat('ccc')}</Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: today ? 'primary.main' : 'text.primary' }}>{dt.toFormat('d')}</Typography>
        </ButtonBase>
      );
    })}

    {units.map((unit) => (
      <Fragment key={unit.id}>
        <Box sx={{ position: 'sticky', left: 0, zIndex: 11, px: 1.25, py: 1, display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, bgcolor: 'background.paper', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>
          <BuildOutlined sx={{ flex: 'none', mt: 0.25, fontSize: 16, color: '#3B2E1A' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' }}>{unit.name}</Typography>
        </Box>
        {days.map((day) => {
          const dt = DateTime.fromJSDate(day);
          const key = dt.toFormat('yyyy-MM-dd');
          const list = byDay[key]?.[unit.id] ?? [];
          return (
            <Box
              key={`${unit.id}-${key}`}
              sx={{ minWidth: 0, minHeight: 64, p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: key === todayKey ? '#FFFAF3' : 'transparent', borderLeft: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}
            >
              {list.length === 0 ? (
                <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>Free</Typography>
              ) : (
                list.map((res) => {
                  const color = res.vetId ? providerColor[res.vetId] : undefined;
                  const ink = color?.fg ?? '#374151';
                  const time = rangeText(res.startMin, res.endMin);
                  return (
                    <ButtonBase
                      key={res.key}
                      onClick={() => onOpenAppointment(res.members[0])}
                      aria-label={`${unit.name}, ${dt.toFormat('cccc')}, ${res.vetName}, ${time}, ${res.what}${res.where ? `, at ${res.where}` : ''}`}
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left', px: 0.75, py: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: alpha(ink, 0.35), bgcolor: color?.bg ?? '#F3F4F6', color: ink, minWidth: 0 }}
                    >
                      <Typography noWrap sx={{ fontSize: '0.68rem', fontWeight: 500 }}>{time}</Typography>
                      <Typography noWrap sx={{ fontSize: '0.74rem', fontWeight: 600 }}>{res.vetName}</Typography>
                      {res.where && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, fontSize: '0.66rem', fontWeight: 600, color: '#7A4314', minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          <PlaceOutlined sx={{ fontSize: 11, flex: 'none' }} />
                          {res.where}
                        </Box>
                      )}
                    </ButtonBase>
                  );
                })
              )}
            </Box>
          );
        })}
      </Fragment>
    ))}
  </Box>
);

export default EquipmentDayGrid;
